import React, { useState } from "react";
import { type UsernamePasswordLoginRequest, useGetSelfQuery, useLoginMutation } from "../../api/etApi";
import { useAppDispatch } from "../../app/hooks";
import { updateUserInfoDataFromApi } from "../priceCalculatorForm/PriceCalculatorFormSlice";

interface LoginProps {
  onLoginSuccess: () => void;
}

const validateEmail = (email: string) => {
  // Simple email regex
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [login] = useLoginMutation();
  const {data:selfData, error: _, refetch } = useGetSelfQuery(undefined, { refetchOnMountOrArgChange: true });

  React.useEffect(() => {
    if (selfData) {
      dispatch(updateUserInfoDataFromApi(selfData));
    }
  }, [selfData, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateEmail(email)) {
      setError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }
    if (password.length <= 6) {
      setError("Das Passwort muss länger als 6 Zeichen sein.");
      return;
    }

    setLoading(true);
    try {

      const LoginDTO : UsernamePasswordLoginRequest = {
        Username: email,
        Password: password,
        SetTokensAsCookieInstead: true
      }

      await login({ usernamePasswordLoginRequest: LoginDTO }).unwrap();
      refetch();
      onLoginSuccess();
    } catch (err) {
      setError("Login fehlgeschlagen. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className=""
      autoComplete="off"
    >
      <div className="mb-4">
        <label
          htmlFor="login-email"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
        >
          E-Mail
        </label>
        <input
          id="login-email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={loading}
          required
          className={`mt-1 block w-full rounded-md border ${
            error && !validateEmail(email) ? "border-red-500" : "border-gray-300"
          } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
          placeholder="Ihre E-Mail-Adresse"
        />
      </div>
      <div className="mb-4">
        <label
          htmlFor="login-password"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
        >
          Passwort
        </label>
        <input
          id="login-password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          disabled={loading}
          required
          className={`mt-1 block w-full rounded-md border ${
            error && password.length <= 6 ? "border-red-500" : "border-gray-300"
          } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
          placeholder="Ihr Passwort"
        />
      </div>
      {error && (
        <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>
      )}
      <button
        type="submit"
        disabled={loading}
        className={`w-full px-4 py-2 rounded-md text-white font-semibold shadow-sm transition-colors ${
          loading
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-cuno-500 hover:bg-cuno-600 dark:bg-blue-500 dark:hover:bg-blue-600"
        }`}
      >
        {loading ? "Login läuft..." : "Login"}
      </button>
    </form>
  );
};

export default Login;